<script lang="ts">
	import { appStore } from '../stores/appStore';
	import { setTokenStorage } from '../utils/localStorage';
	import type { AppApiClient } from '../api_contracts/app.client';
	import { onMount } from 'svelte';

	let client: AppApiClient | null = $state(null);
	let loading = $state(true);
	let error = $state('');
	let message = $state('Processing OAuth login...');

	// Subscribe to store
	appStore.subscribe((state) => {
		client = state.client;
	});

	onMount(() => {
		handleOAuthCallback();
	});

	async function handleOAuthCallback() {
		if (!client) {
			error = 'Client not initialized';
			loading = false;
			return;
		}

		try {
			// Get URL parameters
			const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
			const code = urlParams.get('code');
			const state = urlParams.get('state');
			const provider = window.location.hash.includes('google') ? 'google' : 'github';

			if (!code) {
				error = 'No authorization code received';
				loading = false;
				return;
			}

			// Verify state token (CSRF protection)
			const savedState = localStorage.getItem('oauth_state');
			if (savedState && savedState !== state) {
				error = 'Invalid state token - possible CSRF attack';
				loading = false;
				return;
			}

			// Remove state token
			localStorage.removeItem('oauth_state');

			// Call appropriate callback handler
			const result =
				provider === 'google'
					? await client.handleGoogleOAuthCallback({ code, state: state || '' })
					: await client.handleGitHubOAuthCallback({ code, state: state || '' });

			if (result.response.successFull) {
				if (result.response.userExists) {
					// User exists and is logged in
					message = 'Login successful! Redirecting...';

					// Store tokens
					if (result.response.token) {
						setTokenStorage(result.response.token);
						appStore.setToken(result.response.token);
					}

					// Fetch and store profile
					if (result.response.token) {
						try {
							const profileResult = await client.getUserProfile({
								token: result.response.token
							});
							if (
								profileResult.response.response.successFull &&
								profileResult.response.profile
							) {
								appStore.setProfile(profileResult.response.profile);
							}
						} catch (e) {
							console.error('Error fetching profile:', e);
						}
					}

					// Redirect to home
					setTimeout(() => {
						window.location.hash = '/';
					}, 1000);
				} else {
					// User doesn't exist, redirect to registration with pre-filled data
					message = 'Account not found. Redirecting to registration...';

					// Store OAuth data for pre-filling registration form
					const oauthData = {
						email: result.response.email,
						firstName: result.response.firstName,
						lastName: result.response.lastName,
						avatarUrl: result.response.avatarUrl,
						provider: result.response.oauthProvider,
						providerId: result.response.oauthProviderId,
						// Register reads this to prefill the form. Stamped so an
						// abandoned sign-up cannot silently prefill a later visit.
						ts: Date.now()
					};
					localStorage.setItem('oauth_registration_data', JSON.stringify(oauthData));

					// Redirect to registration
					setTimeout(() => {
						window.location.hash = '/register';
					}, 1500);
				}
			} else {
				error = result.response.reason || 'OAuth authentication failed';
				loading = false;
			}
		} catch (e: any) {
			error = e.message || 'Error during OAuth callback';
			console.error('OAuth callback error:', e);
			loading = false;
		}
	}
</script>

<div class="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4">
	<div class="max-w-md w-full">
		<div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
			{#if loading}
				<div class="mb-6">
					<svg
						class="w-16 h-16 mx-auto text-blue-600 dark:text-blue-400 animate-spin"
						fill="none"
						viewBox="0 0 24 24"
					>
						<circle
							class="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							stroke-width="4"
						></circle>
						<path
							class="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
						></path>
					</svg>
				</div>
				<h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{message}</h2>
				<p class="text-gray-600 dark:text-gray-400">Please wait...</p>
			{:else if error}
				<div class="mb-6">
					<svg
						class="w-16 h-16 mx-auto text-red-600 dark:text-red-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
						/>
					</svg>
				</div>
				<h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
					Authentication Failed
				</h2>
				<p class="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
				<a
					href="#/login"
					class="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
				>
					Back to Login
				</a>
			{/if}
		</div>
	</div>
</div>
